import { createContext, useContext } from 'react'
import type { CurrentUser } from './authApi'

export type AuthStatus = 'loading' | 'anonymous' | 'authenticated'

export interface AuthValue {
  status: AuthStatus
  user: CurrentUser | null
  roles: string[]
  login: (identifier: string, password: string) => Promise<CurrentUser>
  logout: () => Promise<void>
  /** Re-read /auth/me (e.g. after the office changed roles). */
  reload: () => Promise<void>
  /** True when the last session ended because the refresh token was rejected. */
  expired: boolean
}

export const AuthContext = createContext<AuthValue | null>(null)

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}

/**
 * True when `user` may do `permission` (`resource:action`, from GET /auth/permissions). Without a
 * permissions list (older backend) every action is allowed and the server's 403 is the guard.
 */
export function userCan(user: Pick<CurrentUser, 'permissions'> | null, permission: string): boolean {
  if (!user) return false
  if (!user.permissions) return true
  return user.permissions.includes(permission)
}

/** Permission check for console actions. */
export function usePermission(permission: string): boolean {
  const { user } = useAuth()
  return userCan(user, permission)
}

/** A checker for many permissions in one component: `const can = useCan(); can('units:hold')`. */
export function useCan(): (permission: string) => boolean {
  const { user } = useAuth()
  return (permission: string) => userCan(user, permission)
}
